import { SeverityGroup } from '../../../services/api';

/** Darstellung des Identifier-Katalogs — nach Fehler-Gruppen gegliedert oder flach. */
export type SeverityCatalogView = 'groups' | 'list';

/**
 * Ausgangs-Darstellung. Gruppiert, weil der Katalog mit >100 Identifiern flach
 * nicht mehr überschaubar ist und die Gruppe die Severity trägt.
 */
export const DEFAULT_SEVERITY_CATALOG_VIEW: SeverityCatalogView = 'groups';

/** Schlüssel der gemerkten Darstellung — reine Anzeige-Vorliebe, pro Browser. */
const VIEW_STORAGE_KEY = 'severity-catalog-view';

/**
 * Sektions-Schlüssel der Sammel-Sektion „ohne Gruppe".
 *
 * Bewusst eigenständig gegenüber `UNGROUPED_KEY` der Master-Spalte: dort ist es
 * eine Auswahl, hier eine Sektion der Tabelle.
 */
export const UNGROUPED_SECTION_KEY = '__ungrouped_section__';

/** Type-Guard für den aus `localStorage` gelesenen Rohwert. */
export function isSeverityCatalogView(value: string): value is SeverityCatalogView {
  return value === 'groups' || value === 'list';
}

/**
 * Liest die zuletzt gewählte Darstellung.
 *
 * Fällt auf {@link DEFAULT_SEVERITY_CATALOG_VIEW} zurück, wenn nichts gemerkt
 * ist oder der Zugriff scheitert (Privat-Modus, blockierte Site-Daten).
 */
export function loadSeverityCatalogView(): SeverityCatalogView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored !== null && isSeverityCatalogView(stored)) return stored;
  } catch {
    // Kein Zugriff auf localStorage — die Vorliebe ist entbehrlich.
  }
  return DEFAULT_SEVERITY_CATALOG_VIEW;
}

/** Merkt die gewählte Darstellung; schlägt der Zugriff fehl, bleibt es beim Default. */
export function saveSeverityCatalogView(view: SeverityCatalogView): void {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Siehe loadSeverityCatalogView.
  }
}

/** Eine Gruppe samt ihrer Katalog-Zeilen in der Gruppen-Ansicht. */
export interface SeverityCatalogSection<TRow> {
  /** Gruppen-ID bzw. {@link UNGROUPED_SECTION_KEY} — React-Key und Aufklapp-Schlüssel. */
  key: string;
  /** `null` in der Sammel-Sektion der Identifier ohne Gruppe. */
  group: SeverityGroup | null;
  rows: TRow[];
}

/**
 * Ordnet Katalog-Zeilen ihren Gruppen zu — Reihenfolge wie `groups`, die
 * Sammel-Sektion „ohne Gruppe" immer am Ende.
 *
 * `hideEmptyGroups` blendet mitgliederlose Gruppen aus: bei aktivem Filter sind
 * sie Rauschen, ohne Filter sind sie das Signal „hier fehlt noch die Zuordnung".
 * Die Sammel-Sektion erscheint nie leer — sie hat ohne Mitglieder keine Aussage.
 */
export function groupCatalogRows<TRow extends { groupId: string | null }>(
  rows: TRow[],
  groups: SeverityGroup[],
  hideEmptyGroups: boolean
): SeverityCatalogSection<TRow>[] {
  const byGroup = new Map<string, TRow[]>();
  const ungrouped: TRow[] = [];

  for (const row of rows) {
    if (row.groupId === null) {
      ungrouped.push(row);
      continue;
    }
    const bucket = byGroup.get(row.groupId);
    if (bucket) bucket.push(row);
    else byGroup.set(row.groupId, [row]);
  }

  const sections: SeverityCatalogSection<TRow>[] = [];
  for (const group of groups) {
    const groupRows = byGroup.get(group.id) ?? [];
    if (groupRows.length === 0 && hideEmptyGroups) continue;
    sections.push({ key: group.id, group, rows: groupRows });
  }

  if (ungrouped.length > 0) {
    sections.push({ key: UNGROUPED_SECTION_KEY, group: null, rows: ungrouped });
  }

  return sections;
}
